import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  useCreateSalesOrderMutation,
  useCreateHoldMutation,
  useGetSalesOrderQuery,
  useGetCustomersQuery,
  useRecordSalesPaymentMutation,
  useUpdateSalesOrderStatusMutation,
  useHoldSalesOrderMutation,
  useUnholdSalesOrderMutation,
} from '../../../store/api/salesApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetPaymentAccountsQuery } from '../../../store/api/purchaseApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetLocationsQuery } from '../../../store/api/inventoryApi';
import { useGetUsersQuery } from '../../../store/api/userApi';
import { CustomerFormContent } from './CustomerForm';
import { selectUser } from '../../../store/slices/authSlice';
import { useGetMyActiveTripQuery } from '../../../store/api/vanSalesApi';
import Chip from '@mui/material/Chip';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { formatItemDisplayName } from '../utils/formatItemDisplayName';
import { shareSalesOrderPdf } from '../utils/exportSalesOrderPdf';

const DEFAULT_VAT_PERCENT = 5;

function SalesOrderForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isView = !!id && id !== 'new';

  const currentUser = useSelector(selectUser);

  const outletContext = useOutletContext();
  const setNavigationBlocked = outletContext?.setNavigationBlocked;

  const [createSalesOrder] = useCreateSalesOrderMutation();
  const [createHold] = useCreateHoldMutation();
  const [updateSalesOrderStatus] = useUpdateSalesOrderStatusMutation();
  const [holdSalesOrder] = useHoldSalesOrderMutation();
  const [unholdSalesOrder] = useUnholdSalesOrderMutation();
  const { data: customers } = useGetCustomersQuery();
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] =
    useLazyGetProductsQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const { data: locations } = useGetLocationsQuery();
  const { data: usersData } = useGetUsersQuery();
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const userList = Array.isArray(usersData) ? usersData : usersData?.users ?? [];
  const { data: existingOrder, isLoading: loadingOrder } = useGetSalesOrderQuery(id, {
    skip: !isView,
  });
  const { data: activeVanTrip } = useGetMyActiveTripQuery(undefined, { skip: isView });

  const vanMode = !isView && activeVanTrip && activeVanTrip.id;
  const vanLocationId = activeVanTrip?.van?.locationId || activeVanTrip?.van?.location?.id;
  const vanName = activeVanTrip?.van?.name;
  const vanPlate = activeVanTrip?.van?.plateNumber;
  const vanTripNumber = activeVanTrip?.tripNumber;

  const currency = company?.currency || 'USD';
  const vatIncluded = company?.salesVatIncluded !== false;
  const defaultVat = Number(company?.vatRate) || DEFAULT_VAT_PERCENT;
  const itemNameFormat = company?.printItemNameFormat || 'name_sku';

  const previewInvoiceNumber = (() => {
    const prefix = String(company?.invoicePrefix || 'INV').trim() || 'INV';
    const nextNum = Number(company?.invoiceNextNumber || 1) || 1;
    const paddingRaw = Number(company?.invoiceNumberPadding);
    const padding = Number.isInteger(paddingRaw) ? Math.min(12, Math.max(0, paddingRaw)) : 6;
    const numberPart = String(Math.max(1, nextNum)).padStart(padding, '0');
    return `${prefix}-${numberPart}`;
  })();

  const [formData, setFormData] = useState(() => ({
    customerId: '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    orderDate: new Date().toISOString().split('T')[0],
    salesType: 'credit',
    creditDays: '',
    salesPersonId: currentUser?.id || '',
    locationId: '',
    notes: '',
  }));

  const creditDaysOptions = (company?.creditDaysOptions || '7,14,30')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0);

  const [items, setItems] = useState([]);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const [scanError, setScanError] = useState('');
  const [pendingBarcode, setPendingBarcode] = useState('');
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    existingIndex: -1,
    product: null,
  });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  useEffect(() => {
    if (setNavigationBlocked && !isView) {
      setNavigationBlocked(items.length > 0);
    }
    if (isView) return;
    const handleBeforeUnload = (e) => {
      if (items.length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.history.pushState({ isForm: true }, '', window.location.href);
    const handlePopState = (e) => {
      if (items.length > 0) {
        window.history.pushState({ isForm: true }, '', window.location.href);
        setCancelDialogOpen(true);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      if (setNavigationBlocked) setNavigationBlocked(false);
    };
  }, [items, isView, setNavigationBlocked]);
  const [payments, setPayments] = useState(
    !!id && id !== 'new'
      ? []
      : [
          {
            method: 'cash',
            paymentAccountId: '',
            referenceNo: '',
            amount: '',
          },
        ]
  );
  const [autoPaymentAmount, setAutoPaymentAmount] = useState(true);
  const [billDiscount, setBillDiscount] = useState(0);
  const submitModeRef = useRef('save'); // 'save' | 'save-print' | 'quotation' - read in handleSubmit
  const [receivePaymentOpen, setReceivePaymentOpen] = useState(false);
  const [receivePaymentForm, setReceivePaymentForm] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    amount: '',
    paymentMethod: 'cash',
    paymentAccountId: '',
    referenceNo: '',
    notes: '',
  });
  const [recordSalesPayment, { isLoading: recordingPayment }] = useRecordSalesPaymentMutation();

  const selectedCustomer = customers?.find((c) => c.id === formData.customerId);

  useEffect(() => {
    if (!scanOpen) {
      setScanError('');
      setPendingBarcode('');
    }
  }, [scanOpen]);

  const handleAddProductRow = (product) => {
    if (!product) return;
    setItems((prev) => {
      const existingIndex = prev.findIndex((it) => String(it.productId) === String(product.id));
      if (existingIndex !== -1) {
        setDuplicateDialog({ open: true, existingIndex, product });
        return prev;
      }
      return [
        ...prev,
        {
          productId: product.id,
          productName: product.name,
          productSku: product.sku,
          productBrand: product.brand,
          productBarcode: product.barcode,
          maxDiscountType:
            product.maxDiscountType === 'percent' || product.maxDiscountType === 'amount'
              ? product.maxDiscountType
              : undefined,
          maxDiscountPercent: product.maxDiscountPercent != null ? product.maxDiscountPercent : undefined,
          maxDiscountAmount: product.maxDiscountAmount != null ? product.maxDiscountAmount : undefined,
          quantity: 1,
          unit: 'pcs',
          unitPrice: product.sellingPrice ?? 0,
          discount: 0,
          vatPercent: defaultVat,
        },
      ];
    });
  };

  useEffect(() => {
    if (!pendingBarcode) return;
    const list = productSearchData?.products || [];
    if (!Array.isArray(list) || list.length === 0) return;

    const code = String(pendingBarcode).trim();
    if (!code) return;

    const match = list.find((p) => String(p?.barcode || '').trim() === code);
    if (match) {
      handleAddProductRow(match);
      setProductSearch('');
      setPendingBarcode('');
      setScanOpen(false);
    }
  }, [pendingBarcode, productSearchData, handleAddProductRow]);

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const computeLine = (item) => {
    const qty = toNum(item.quantity);
    const price = toNum(item.unitPrice);
    const discount = toNum(item.discount);
    const vatPct = toNum(item.vatPercent);
    const lineSubtotal = Math.max(0, qty * price - discount);
    let vatAmount = 0;
    if (vatPct > 0) {
      if (vatIncluded) {
        const net = lineSubtotal / (1 + vatPct / 100);
        vatAmount = lineSubtotal - net;
      } else {
        vatAmount = lineSubtotal * (vatPct / 100);
      }
    }
    const lineTotal = vatIncluded ? lineSubtotal : lineSubtotal + vatAmount;
    return {
      vatAmount: Math.round(vatAmount * 100) / 100,
      lineTotal: Math.round(lineTotal * 100) / 100,
      lineSubtotal,
    };
  };

  const orderSubtotal = items.reduce(
    (sum, item) => sum + toNum(item.quantity) * toNum(item.unitPrice),
    0
  );
  const lineDiscountTotal = items.reduce((sum, item) => sum + toNum(item.discount), 0);
  const orderDiscountTotal = lineDiscountTotal + toNum(billDiscount);
  const orderVatTotal = items.reduce((sum, item) => sum + computeLine(item).vatAmount, 0);
  const netOrder = orderSubtotal - orderDiscountTotal;
  const grandTotal = vatIncluded ? netOrder : netOrder + orderVatTotal;

  const paidTotal = payments.reduce(
    (sum, p) => sum + (Number.isFinite(Number(p.amount)) ? Number(p.amount) : 0),
    0
  );
  const remaining = grandTotal - paidTotal;
  const change = paidTotal - grandTotal;

  // If creating a new order and there is a logged-in user, default them as sales person
  useEffect(() => {
    if (!isView && currentUser?.id && !formData.salesPersonId) {
      setFormData((prev) => ({ ...prev, salesPersonId: currentUser.id }));
    }
  }, [isView, currentUser, formData.salesPersonId]);

  // Van mode: auto-set locationId and salesType to cash when user has active van trip
  useEffect(() => {
    if (vanMode && vanLocationId && !isView) {
      setFormData((prev) => ({
        ...prev,
        locationId: vanLocationId,
        salesType: prev.salesType || 'cash',
      }));
    }
  }, [vanMode, vanLocationId, isView]);

  useEffect(() => {
    if (existingOrder && isView) {
      setFormData({
        customerId: existingOrder.customerId || '',
        invoiceNumber: existingOrder.invoiceNumber || '',
        invoiceDate: existingOrder.invoiceDate
          ? existingOrder.invoiceDate.substring(0, 10)
          : formData.invoiceDate,
        orderDate: existingOrder.orderDate
          ? existingOrder.orderDate.substring(0, 10)
          : formData.orderDate,
        salesType: existingOrder.salesType || 'credit',
        creditDays: existingOrder.creditDays != null ? String(existingOrder.creditDays) : '',
        salesPersonId: existingOrder.salesPersonId || '',
        locationId: existingOrder.locationId || '',
        notes: existingOrder.notes || '',
      });
      const mappedItems = (existingOrder.items || []).map((it) => ({
          productId: it.productId,
          productName: it.product?.name,
          productSku: it.product?.sku,
          productBrand: it.product?.brand,
          productBarcode: it.product?.barcode,
          maxDiscountType: it.product?.maxDiscountType === 'percent' || it.product?.maxDiscountType === 'amount' ? it.product.maxDiscountType : undefined,
          maxDiscountPercent: it.product?.maxDiscountPercent != null ? it.product.maxDiscountPercent : undefined,
          maxDiscountAmount: it.product?.maxDiscountAmount != null ? it.product.maxDiscountAmount : undefined,
          quantity: it.quantity,
          unit: it.unit || 'pcs',
          unitPrice: it.unitPrice,
          discount: toNum(it.discount),
          vatPercent: toNum(it.vatPercent) || defaultVat,
          batchNo: it.batchNo || '',
          expiryDate: it.expiryDate ? it.expiryDate.substring(0, 10) : '',
        }));
      setItems(mappedItems);
      const lineDiscountTotal = mappedItems.reduce((sum, it) => sum + toNum(it.discount), 0);
      const orderDiscount = toNum(existingOrder.discountTotal);
      const extra = orderDiscount - lineDiscountTotal;
      setBillDiscount(extra > 0 ? extra : 0);
      const paid = toNum(existingOrder.paidAmount);
      if (paid > 0) {
        setPayments([
          { method: 'cash', paymentAccountId: '', referenceNo: '', amount: paid },
        ]);
        setAutoPaymentAmount(false);
      }
    }
  }, [existingOrder, isView]);

  // For new cash sales, while the user hasn't manually edited payments,
  // keep the single payment row synced to the full grand total.
  useEffect(() => {
    if (
      !isView &&
      formData.salesType === 'cash' &&
      payments.length === 1 &&
      autoPaymentAmount
    ) {
      const target = grandTotal.toFixed(2);
      if (payments[0].amount !== target) {
        const next = [...payments];
        next[0] = { ...next[0], amount: target };
        setPayments(next);
      }
    }
  }, [grandTotal, formData.salesType, isView, payments, autoPaymentAmount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) {
      navigate('/sales');
      return;
    }
    const mode = submitModeRef.current;
    submitModeRef.current = 'save';
    const isCreateAndHold = mode === 'save-hold';
    if (!isCreateAndHold) {
      if (!formData.customerId) {
        alert('Customer is required');
        return;
      }
      if (!formData.invoiceDate) {
        alert('Invoice Date is required');
        return;
      }
      if (!items.length) {
        alert('Please add at least one item');
        return;
      }
      if (items.some((i) => !i.productId)) {
        alert('Please select a product for all items');
        return;
      }
      if (formData.salesType === 'cash' && paidTotal + 0.0001 < grandTotal) {
        alert('For cash sales, paid amount must be at least the grand total.');
        return;
      }
    } else {
      if (!formData.invoiceDate) {
        alert('Invoice Date is required');
        return;
      }
      if (!items.length) {
        alert('Please add at least one item');
        return;
      }
      if (items.some((i) => !i.productId)) {
        alert('Please select a product for all items');
        return;
      }
    }

    const globalMaxType = company?.maxDiscountType === 'percent' || company?.maxDiscountType === 'amount' ? company.maxDiscountType : null;
    const globalMaxPct = company?.maxDiscountPercent != null ? Number(company.maxDiscountPercent) : null;
    const globalMaxAmt = company?.maxDiscountAmount != null ? Number(company.maxDiscountAmount) : null;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const lineMaxType =
        item.maxDiscountType === 'percent' || item.maxDiscountType === 'amount' ? item.maxDiscountType : globalMaxType;
      const lineMaxPct = item.maxDiscountPercent != null ? Number(item.maxDiscountPercent) : globalMaxPct;
      const lineMaxAmt = item.maxDiscountAmount != null ? Number(item.maxDiscountAmount) : globalMaxAmt;
      const discount = toNum(item.discount);
      if (lineMaxType === 'percent' && lineMaxPct != null && lineMaxPct > 0) {
        const qty = toNum(item.quantity);
        const price = toNum(item.unitPrice);
        const lineTotalBeforeDiscount = qty * price;
        if (lineTotalBeforeDiscount > 0 && discount > 0) {
          const lineDiscountPct = (discount / lineTotalBeforeDiscount) * 100;
          if (lineDiscountPct > lineMaxPct) {
            alert(
              `Maximum discount is ${lineMaxPct}%. Line ${i + 1} (${item.productName || 'item'}) has ${lineDiscountPct.toFixed(1)}% discount.`
            );
            return;
          }
        }
      }
      if (lineMaxType === 'amount' && lineMaxAmt != null && lineMaxAmt >= 0 && discount > lineMaxAmt) {
        alert(
          `Maximum discount per line is ${lineMaxAmt} ${currency}. Line ${i + 1} (${item.productName || 'item'}) has discount ${discount}.`
        );
        return;
      }
    }

    try {
      if (mode === 'save-hold') {
        const created = await createHold({
          ...formData,
          invoiceDate: formData.invoiceDate,
          orderDate: formData.orderDate || formData.invoiceDate,
          salesType: formData.salesType,
          creditDays:
            formData.salesType === 'credit'
              ? (formData.creditDays ? parseInt(formData.creditDays, 10) : undefined)
              : undefined,
          salesPersonId: formData.salesPersonId || undefined,
          locationId: formData.locationId || undefined,
          billDiscount: Number(billDiscount) || 0,
          payments: payments.map((p) => ({
            method: p.method,
            paymentAccountId: p.paymentAccountId || undefined,
            referenceNo: p.referenceNo || undefined,
            amount: Number(p.amount || 0),
          })),
          items: items.map((item) => {
            const { vatAmount } = computeLine(item);
            return {
              productId: item.productId,
              quantity: parseFloat(item.quantity),
              unit: item.unit || undefined,
              unitPrice: parseFloat(item.unitPrice),
              discount: toNum(item.discount),
              vatPercent: toNum(item.vatPercent) || defaultVat,
              vatAmount,
              batchNo: item.batchNo || undefined,
              expiryDate: item.expiryDate || undefined,
            };
          }),
        }).unwrap();
        navigate(created?.id ? `/sales/holds/${created.id}` : '/sales/holds');
        return;
      }
      const created = await createSalesOrder({
        ...formData,
        invoiceNumber: formData.invoiceNumber?.trim() || undefined,
        invoiceDate: formData.invoiceDate,
        orderDate: formData.orderDate || formData.invoiceDate,
        salesType: formData.salesType,
        creditDays:
          formData.salesType === 'credit'
            ? (formData.creditDays ? parseInt(formData.creditDays, 10) : undefined)
            : undefined,
        salesPersonId: formData.salesPersonId || undefined,
        locationId: formData.locationId || undefined,
        billDiscount: Number(billDiscount) || 0,
        payments: payments.map((p) => ({
          method: p.method,
          paymentAccountId: p.paymentAccountId || undefined,
          referenceNo: p.referenceNo || undefined,
          amount: Number(p.amount || 0),
        })),
        items: items.map((item) => {
          const { vatAmount, lineTotal } = computeLine(item);
          return {
            productId: item.productId,
            quantity: parseFloat(item.quantity),
            unit: item.unit || undefined,
            unitPrice: parseFloat(item.unitPrice),
            discount: toNum(item.discount),
            vatPercent: toNum(item.vatPercent) || defaultVat,
            vatAmount,
            batchNo: item.batchNo || undefined,
            expiryDate: item.expiryDate || undefined,
          };
        }),
      }).unwrap();
      if (mode === 'save-print' && created?.id) {
        navigate(`/sales/orders/${created.id}/print`);
      } else {
        navigate('/sales');
      }
    } catch (error) {
      alert(error?.data?.message || 'Error creating sales order');
    }
  };

  if (isView && loadingOrder) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          {isView ? 'Sales Order Details' : 'New Sales Order'}
        </Typography>
        {isView && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label="Status"
              value={existingOrder?.status ?? ''}
              onChange={(e) => {
                const newStatus = e.target.value;
                if (newStatus && id) updateSalesOrderStatus({ id, status: newStatus });
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="quotation">Quotation</MenuItem>
              <MenuItem value="on_hold">On hold</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="shipped">Shipped</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => {
                if (existingOrder && company) {
                  shareSalesOrderPdf(existingOrder, company, {
                    itemFormat: company.printItemNameFormat || 'name_sku',
                    filename: (existingOrder.invoiceNumber || existingOrder.orderNumber || 'invoice').replace(
                      /\s+/g,
                      '-'
                    ),
                  });
                }
              }}
              disabled={!existingOrder || !company}
              sx={{ borderRadius: 2 }}
            >
              Share
            </Button>
            <Button variant="outlined" onClick={() => navigate(`/sales/orders/${id}/print`)} sx={{ borderRadius: 2 }}>
              Print Invoice
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!existingOrder || existingOrder?.status === 'on_hold' || Number(existingOrder?.balanceAmount || 0) <= 0}
              title={existingOrder?.status === 'on_hold' ? 'Unhold the order first to record payment' : ''}
              onClick={() => {
                const balance = Number(existingOrder?.balanceAmount || 0) || 0;
                setReceivePaymentForm((prev) => ({
                  ...prev,
                  paymentDate: new Date().toISOString().split('T')[0],
                  amount: balance > 0 ? balance.toFixed(2) : '',
                  paymentMethod: 'cash',
                  paymentAccountId: '',
                  referenceNo: '',
                  notes: '',
                }));
                setReceivePaymentOpen(true);
              }}
              sx={{ borderRadius: 2 }}
            >
              Receive Payment
            </Button>
          </Box>
        )}
      </Box>

      {vanMode && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            bgcolor: '#E3F2FD',
            border: '1px solid #90CAF9',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <LocalShippingIcon sx={{ color: '#1565C0', fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1565C0' }}>
              Van Sales Mode
            </Typography>
            <Typography variant="body2" sx={{ color: '#1565C0' }}>
              Van: {vanName} ({vanPlate}) &bull; Trip: {vanTripNumber} &bull;
              Sales will be made from the van&apos;s stock and automatically linked to this trip.
            </Typography>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  select
                  label="Customer Name *"
                  name="customerId"
                  value={formData.customerId}
                  onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                  required
                >
                  <MenuItem value="">Select Customer</MenuItem>
                  {customers?.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </TextField>
                {!isView && (
                  <Button
                    variant="outlined"
                    onClick={() => setCustomerDialogOpen(true)}
                    sx={{ height: 56, minWidth: 44 }}
                    title="Add Customer"
                  >
                    <PersonAddAltIcon fontSize="small" />
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Customer TRN (if VAT registered)"
                value={selectedCustomer?.trn ?? ''}
                InputProps={{ readOnly: true }}
                placeholder="From customer"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Invoice Number"
                name="invoiceNumber"
                value={!isView && !formData.invoiceNumber ? previewInvoiceNumber : formData.invoiceNumber}
                disabled
                helperText={
                  isView
                    ? 'Invoice number is not editable'
                    : 'Auto-generated from Settings on save'
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Invoice Date *"
                name="invoiceDate"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                disabled={isView}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Sales Type"
                name="salesType"
                value={formData.salesType}
                onChange={(e) => {
                  const next = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    salesType: next,
                    creditDays: next === 'credit' ? prev.creditDays : '',
                  }));
                }}
                disabled={isView}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
              </TextField>
            </Grid>
            {formData.salesType === 'credit' && (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  select
                  label="Credit days (payment due)"
                  name="creditDays"
                  value={formData.creditDays}
                  onChange={(e) => setFormData({ ...formData, creditDays: e.target.value })}
                  disabled={isView}
                  helperText="Days until payment due"
                >
                  <MenuItem value="">None</MenuItem>
                  {creditDaysOptions.map((days) => (
                    <MenuItem key={days} value={String(days)}>
                      {days} days
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Sales Person (optional)"
                name="salesPersonId"
                value={formData.salesPersonId}
                onChange={(e) => setFormData({ ...formData, salesPersonId: e.target.value })}
                disabled={isView}
              >
                <MenuItem value="">None</MenuItem>
                {userList.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email || u.username}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={vanMode ? `Branch / Store (Van: ${vanName})` : 'Branch / Store'}
                name="locationId"
                value={formData.locationId}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                disabled={isView || !!vanMode}
              >
                <MenuItem value="">None</MenuItem>
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name} ({loc.code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Line Items
                </Typography>
                {!isView && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'center',
                      minWidth: { xs: '100%', sm: 420 },
                      maxWidth: 600,
                    }}
                  >
                    <Autocomplete
                      fullWidth
                      size="medium"
                      autoHighlight
                      options={productSearchData?.products || []}
                      loading={searchingProducts}
                      filterOptions={(x) => x}
                      value={null}
                      inputValue={productSearch}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        const label = formatItemDisplayName(
                          { name: option.name, sku: option.sku || option.barcode, brand: option.brand },
                          itemNameFormat === 'name' ? 'name_sku' : itemNameFormat
                        );
                        return label || '';
                      }}
                      onInputChange={(_, value) => {
                        setProductSearch(value);
                        if (value && value.trim().length >= 1) {
                          triggerProductSearch({ search: value.trim(), limit: 20 });
                        }
                      }}
                      onChange={(_, value) => {
                        if (value) {
                          handleAddProductRow(value);
                          setProductSearch('');
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Scan or search item (name / barcode)"
                          placeholder="Type or paste name / barcode"
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 52,
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                    <IconButton
                      onClick={() => setScanOpen(true)}
                      title="Scan barcode"
                      sx={{
                        height: 52,
                        width: 52,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <QrCodeScannerIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600, width: 56 }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Product Name *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Barcode</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Batch No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Quantity *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Selling Price *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Discount
                        <Typography component="span" variant="caption" display="block" color="text.secondary">
                          {company?.maxDiscountType === 'percent' && company?.maxDiscountPercent != null
                            ? `max ${Number(company.maxDiscountPercent)}% (or product)`
                            : company?.maxDiscountType === 'amount' && company?.maxDiscountAmount != null
                              ? `max ${Number(company.maxDiscountAmount)} ${currency} (or product)`
                              : 'max % or amount if set'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>VAT %</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>VAT Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Line Total</TableCell>
                      {!isView && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      const { vatAmount, lineTotal } = computeLine(item);
                      return (
                        <TableRow key={index}>
                          <TableCell sx={{ fontWeight: 500 }}>{index + 1}</TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                              {formatItemDisplayName(
                                { name: item.productName, sku: item.productSku, brand: item.productBrand },
                                itemNameFormat
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>
                            {item.productBarcode ?? '–'}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={item.batchNo ?? ''}
                              onChange={(e) => handleItemChange(index, 'batchNo', e.target.value)}
                              placeholder="If applicable"
                              sx={{ width: 100 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="date"
                              value={item.expiryDate ?? ''}
                              onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 130 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const current = Number(item.quantity || 0) || 0;
                                  const next = Math.max(0.01, current - 1);
                                  handleItemChange(index, 'quantity', String(next));
                                }}
                                disabled={isView}
                                title="Decrease"
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <TextField
                                type="number"
                                size="small"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                sx={{ width: 80 }}
                                inputProps={{ min: 0.01, step: 0.01 }}
                                disabled={isView}
                                required
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const current = Number(item.quantity || 0) || 0;
                                  const next = Math.max(0.01, current + 1);
                                  handleItemChange(index, 'quantity', String(next));
                                }}
                                disabled={isView}
                                title="Increase"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={item.unit ?? 'pcs'}
                              onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                              sx={{ width: 70 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0, step: 0.01 }}
                              disabled={isView}
                              required
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.discount}
                              onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0, step: 0.01 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.vatPercent}
                              onChange={(e) => handleItemChange(index, 'vatPercent', e.target.value)}
                              sx={{ width: 60 }}
                              inputProps={{ min: 0, max: 100, step: 0.01 }}
                              disabled={isView}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{vatAmount.toFixed(2)}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{lineTotal.toFixed(2)}</TableCell>
                          {!isView && (
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {items.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Total Qty: {items.reduce((sum, item) => sum + toNum(item.quantity), 0)}
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 2 }}>
                Order Summary
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Subtotal (${currency})`}
                value={orderSubtotal.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Bill Discount (${currency})`}
                type="number"
                value={billDiscount}
                onChange={(e) => setBillDiscount(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                disabled={isView}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Discount Total (${currency})`}
                value={orderDiscountTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`VAT Total (${currency})`}
                value={orderVatTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Grand Total (${currency})`}
                value={grandTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
                sx={{ '& input': { fontWeight: 700 } }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 2 }}>
                Payment
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Account (optional)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Amount (${currency})`}</TableCell>
                      {!isView && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={p.method}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].method = e.target.value;
                              setPayments(next);
                          setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 120 }}
                            disabled={isView}
                          >
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="bank">Bank</MenuItem>
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="cheque">Cheque</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={p.paymentAccountId || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].paymentAccountId = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 180 }}
                            disabled={isView}
                          >
                            <MenuItem value="">Auto (by method)</MenuItem>
                            {paymentAccounts?.map((acc) => (
                              <MenuItem key={acc.id} value={acc.id}>
                                {acc.code} - {acc.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={p.referenceNo || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].referenceNo = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 140 }}
                            disabled={isView}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={p.amount}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].amount = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ width: 120 }}
                            inputProps={{ min: 0, step: 0.01 }}
                            disabled={isView}
                          />
                        </TableCell>
                        {!isView && (
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setPayments(payments.filter((_, i) => i !== index));
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {!isView && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setPayments((prev) => [
                                ...prev,
                                {
                                  method: 'cash',
                                  paymentAccountId: '',
                                  referenceNo: '',
                                  amount: grandTotal.toFixed(2),
                                },
                              ]);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ mt: 1 }}
                          >
                            Add Payment Method
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} />
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={`Paid Total (${currency})`}
                value={paidTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={remaining >= 0 ? `Remaining (${currency})` : `Change (${currency})`}
                value={Math.abs(remaining >= 0 ? remaining : change).toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
                disabled={isView}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  type={isView ? 'button' : 'submit'}
                  variant="contained"
                  disabled={!isView && items.length === 0}
                  onClick={isView ? () => navigate('/sales') : () => { submitModeRef.current = 'save'; }}
                >
                  {isView ? 'Back to Sales' : 'Create Order'}
                </Button>
                {!isView && (
                  <>
                    <Button type="submit" variant="outlined" color="warning" onClick={() => { submitModeRef.current = 'save-hold'; }}>
                      Create &amp; Hold
                    </Button>
                    <Button type="submit" variant="outlined" onClick={() => { submitModeRef.current = 'save-print'; }}>
                      Save &amp; Print
                    </Button>
                    <Button variant="outlined" onClick={() => setCancelDialogOpen(true)}>
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      <Dialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Customer</DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            overflow: 'visible',
            '& .MuiInputLabel-root': { overflow: 'visible' },
          }}
        >
          <CustomerFormContent
            compact
            onCancel={() => setCustomerDialogOpen(false)}
            onSuccess={(created) => {
              setCustomerDialogOpen(false);
              if (created?.id) {
                setFormData((prev) => ({ ...prev, customerId: created.id }));
              }
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={receivePaymentOpen}
        onClose={() => setReceivePaymentOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Receive Payment</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            type="date"
            label="Payment Date *"
            value={receivePaymentForm.paymentDate}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, paymentDate: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label="Method *"
            value={receivePaymentForm.paymentMethod}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))
            }
            fullWidth
          >
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="bank">Bank</MenuItem>
            <MenuItem value="card">Card</MenuItem>
            <MenuItem value="cheque">Cheque</MenuItem>
          </TextField>
          <TextField
            select
            label="Account (optional)"
            value={receivePaymentForm.paymentAccountId}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({
                ...prev,
                paymentAccountId: e.target.value,
              }))
            }
            fullWidth
          >
            <MenuItem value="">Auto (by method)</MenuItem>
            {paymentAccounts?.map((acc) => (
              <MenuItem key={acc.id} value={acc.id}>
                {acc.code} - {acc.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label={`Amount (${currency}) *`}
            value={receivePaymentForm.amount}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, amount: e.target.value }))
            }
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label="Reference"
            value={receivePaymentForm.referenceNo}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, referenceNo: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label="Notes"
            value={receivePaymentForm.notes}
            onChange={(e) =>
              setReceivePaymentForm((prev) => ({ ...prev, notes: e.target.value }))
            }
            fullWidth
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, pb: 1 }}>
            <Button onClick={() => setReceivePaymentOpen(false)} disabled={recordingPayment}>
              Close
            </Button>
            <Button
              variant="contained"
              disabled={recordingPayment}
              onClick={async () => {
                if (!existingOrder?.id) return;
                const amt = Number(receivePaymentForm.amount || 0);
                if (!amt || amt <= 0) {
                  alert('Amount must be greater than zero');
                  return;
                }
                try {
                  await recordSalesPayment({
                    id: existingOrder.id,
                    paymentDate: receivePaymentForm.paymentDate,
                    amount: amt,
                    paymentMethod: receivePaymentForm.paymentMethod,
                    paymentAccountId:
                      receivePaymentForm.paymentAccountId || undefined,
                    referenceNo: receivePaymentForm.referenceNo || undefined,
                    notes: receivePaymentForm.notes || undefined,
                  }).unwrap();
                  setReceivePaymentOpen(false);
                } catch (err) {
                  alert(err?.data?.message || 'Error recording payment');
                }
              }}
            >
              {recordingPayment ? 'Saving...' : 'Save Payment'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={scanOpen} onClose={() => setScanOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Scan Item Barcode</DialogTitle>
        <DialogContent>
          {scanError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {scanError}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Point the camera at the barcode. The item will be added automatically when matched.
            </Alert>
          )}

          {scanOpen && (
            <BarcodeScannerComponent
              width="100%"
              height={360}
              delay={200}
              constraints={{ facingMode: 'environment' }}
              onUpdate={(err, result) => {
                if (err) {
                  if (err?.name === 'NotAllowedError') {
                    setScanError('Camera permission denied. Please allow camera access and try again.');
                  } else if (err?.name !== 'NotFoundException') {
                    setScanError('Unable to read barcode. Try better lighting or move closer.');
                  }
                  return;
                }
                const text = result?.text ? String(result.text).trim() : '';
                if (!text) return;

                setProductSearch(text);
                setPendingBarcode(text);
                triggerProductSearch({ search: text, limit: 20 });
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setScanOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Entry?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel? Any unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setCancelDialogOpen(false)}>
            No
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
               setCancelDialogOpen(false);
               window.history.back();
               setTimeout(() => navigate('/sales'), 100);
            }}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false, existingIndex: -1, product: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Item already added</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            {duplicateDialog.product
              ? `“${formatItemDisplayName(
                  {
                    name: duplicateDialog.product.name,
                    sku: duplicateDialog.product.sku || duplicateDialog.product.barcode,
                    brand: duplicateDialog.product.brand,
                  },
                  itemNameFormat
                )}” is already in the list.`
              : 'This item is already in the list.'}
          </Typography>
          <Typography color="text.secondary">Do you want to increase the quantity by 1?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setDuplicateDialog({ open: false, existingIndex: -1, product: null })}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const idx = duplicateDialog.existingIndex;
              setItems((prev) => {
                if (idx < 0 || idx >= prev.length) return prev;
                const next = [...prev];
                const currentQty = Number(next[idx]?.quantity || 0) || 0;
                next[idx] = {
                  ...next[idx],
                  quantity: String(Math.max(0.01, currentQty + 1)),
                };
                return next;
              });
              setDuplicateDialog({ open: false, existingIndex: -1, product: null });
              setProductSearch('');
              setPendingBarcode('');
              setScanOpen(false);
            }}
          >
            Yes, increase
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SalesOrderForm;
